<script setup lang="tsx">
import { onMounted, onUnmounted, ref, watchEffect } from "vue";
import {
  ElementEventBusController,
  getInnerText,
  useClipboard,
  useElementEventBus
} from "@zyzgroup/core-web";

export type ContainerType = "block" | "shrink" | "full";

/// 效果触发时机
export type EffectTriggerTiming =
  | "manual"
  | "mouseMove"
  | "click"
  | "immediate";

/// 效果状态
export type EffectStatus = "start" | "end";

// for merge ce shadow-root styles
defineOptions({
  customOptions: {},
  inheritAttrs: true
});

const props = withDefaults(
  defineProps<{
    type?: ContainerType;
    /// 效果触发时机
    effectTriggerTiming?: EffectTriggerTiming;
    /// 是否支持复制文字
    supportCopyText?: boolean;
  }>(),
  {
    type: "block",
    effectTriggerTiming: "manual",
    supportCopyText: false
  }
);

const emits = defineEmits<{
  (event: "ready", controller: ElementEventBusController): void;
  (event: "update", controller: ElementEventBusController): void;
  (event: "clipboard_success", text: string): void;
  (event: "clipboard_error", text: string): void;
  /// 效果
  (event: "start"): void;
  (event: "end"): void;
}>();

let effectStatus: EffectStatus = "start";
const start = () => {
  effectStatus = "start";
  emits("start");
};
const end = () => {
  effectStatus = "end";
  emits("end");
};
const next = () => {
  effectStatus == "start" ? end() : start();
};

const r = ref();

let controller: ElementEventBusController;

let getControllerResolve: (
  value: ElementEventBusController | PromiseLike<ElementEventBusController>
) => void;

let isEmitReady = false;

watchEffect(() => {
  if (!r.value) {
    // not mounted yet, or the element was unmounted (e.g. by v-if)
    return;
  }
  if (controller) {
    return;
  }

  controller = useElementEventBus(r.value);

  controller.eb.on("resize", () => {
    if (isEmitReady) {
      emits("update", controller);
    }
  });

  controller.eb.on("mouseEnter", () => {
    if (props.effectTriggerTiming == "mouseMove") {
      next();
    }
  });

  controller.eb.on("mouseLeave", () => {
    if (props.effectTriggerTiming == "mouseMove") {
      next();
    }
  });

  controller.eb.on("tap", () => {
    if (props.effectTriggerTiming == "click") {
      next();
    }
    if (props.supportCopyText) {
      const text = getInnerText(r.value);
      useClipboard(text)
        .then(() => {
          emits("clipboard_success", text);
        })
        .catch((err) => {
          emits("clipboard_error", err.toString());
        });
    }
  });

  if (props.effectTriggerTiming !== "manual") {
    start();
  }

  if (props.effectTriggerTiming == "immediate") {
    next();
  }

  controller.start().then(() => {
    getControllerResolve?.(controller);
    emits("ready", controller);
    isEmitReady = true;
  });
});

onMounted(() => {});

onUnmounted(() => {
  controller?.end();
});

// 不推荐使用，只推荐使用 @ready + @update
defineExpose({
  getController: () =>
    new Promise<ElementEventBusController>((resolve) => {
      if (controller) {
        return resolve(controller);
      }
      getControllerResolve = resolve;
    }),
  getElement: () => r.value,
  getRect: () => r.value.getBoundingClientRect()
});
</script>

<template>
  <div
    ref="r"
    class="container"
    :style="{
      position: 'relative',
      boxSizing: 'border-box',
      display: props.type == 'shrink' ? 'inline-block' : 'block',
      width: props.type == 'full' ? '100%' : 'auto',
      height: props.type == 'full' ? '100%' : 'auto'
      /* overflow: 'hidden', */
      /* pointer-events: none; */
    }"
    v-bind="$attrs"
  >
    <slot></slot>
  </div>
</template>
